import { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import { useCategoryStore } from '../../../hooks';
import { Button, Card, CardContent, useToast } from '@lancar-pos/ui';
import { EmojiPicker } from './EmojiPicker';

interface CategoryFormProps {
  categoryId?: string;
  isNew: boolean;
}

const PRESET_COLORS = [
  '#EF4444', // red
  '#F97316', // orange
  '#F59E0B', // amber
  '#84CC16', // lime
  '#10B981', // emerald
  '#06B6D4', // cyan
  '#3B82F6', // blue
  '#6366F1', // indigo
  '#8B5CF6', // violet
  '#D946EF', // fuchsia
  '#EC4899', // pink
  '#6B7280', // gray
];

export function CategoryForm({ categoryId, isNew }: CategoryFormProps) {
  const { categories, addCategory, updateCategory } = useCategoryStore();
  const toast = useToast();
  
  const [isLoading, setIsLoading] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<string>('');
  const [color, setColor] = useState('#3B82F6');
  const [icon, setIcon] = useState('📁');
  const [sortOrder, setSortOrder] = useState('0');

  // Load category data if editing
  useEffect(() => {
    if (!isNew && categoryId) {
      const category = categories.find((c) => c.id === categoryId);
      if (category) {
        setName(category.name);
        setParentId(category.parentId || '');
        setColor(category.color || '#3B82F6');
        setIcon(category.icon || '📁');
        setSortOrder(category.sortOrder.toString());
      }
    }
  }, [isNew, categoryId, categories]);

  // Calculate next sort order for new categories
  const nextSortOrder = useMemo(() => {
    const maxOrder = Math.max(...categories.map((c) => c.sortOrder), -1);
    return maxOrder + 1;
  }, [categories]);

  // Set initial sort order when creating new
  useEffect(() => {
    if (isNew) {
      setSortOrder(nextSortOrder.toString());
    }
  }, [isNew, nextSortOrder]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Nama kategori wajib diisi');
      return;
    }

    setIsLoading(true);

    try {
      const categoryData = {
        name: name.trim(),
        parentId: parentId || null,
        color,
        icon: icon || '📁',
        sortOrder: parseInt(sortOrder, 10) || 0,
      };

      if (isNew) {
        await addCategory(categoryData);
        toast.success('Kategori berhasil ditambahkan');
      } else if (categoryId) {
        await updateCategory(categoryId, categoryData);
        toast.success('Kategori berhasil diperbarui');
      }

      window.history.back();
    } catch (error) {
      toast.error(isNew ? 'Gagal menambahkan kategori' : 'Gagal memperbarui kategori');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter out current category and its children from parent options to prevent circular reference
  const availableParents = useMemo(() => {
    if (isNew || !categoryId) return categories.filter((c) => !c._deleted);
    
    // When editing, exclude self and all descendants
    const getDescendantIds = (parentId: string): string[] => {
      const children = categories.filter((c) => c.parentId === parentId);
      const descendantIds = children.map((c) => c.id);
      children.forEach((child) => {
        descendantIds.push(...getDescendantIds(child.id));
      });
      return descendantIds;
    };

    const excludedIds = new Set([categoryId, ...getDescendantIds(categoryId)]);
    return categories.filter((c) => !c._deleted && !excludedIds.has(c.id));
  }, [categories, categoryId, isNew]);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">
              {isNew ? 'Tambah Kategori' : 'Edit Kategori'}
            </h1>
          </div>
          <Button onClick={handleSave} loading={isLoading}>
            <Save className="w-4 h-4 mr-2" />
            Simpan
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 lg:p-6 space-y-6">
        <Card>
          <CardContent className="p-6 space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nama Kategori *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Contoh: Makanan"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Parent Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategori Induk
              </label>
              <select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Tanpa induk (kategori utama)</option>
                {availableParents.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Pilih kategori induk untuk membuat sub-kategori
              </p>
            </div>

            {/* Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Warna
              </label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`w-8 h-8 rounded-full transition-transform ${
                      color === c ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                    }`}
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-sm text-gray-600">Kustom:</span>
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer"
                />
                <span className="text-sm font-mono text-gray-600">{color}</span>
              </div>
            </div>

            {/* Icon */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ikon
              </label>
              <EmojiPicker
                value={icon}
                onChange={(emoji) => setIcon(emoji)}
              />
            </div>

            {/* Sort Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Urutan
              </label>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                min="0"
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Angka lebih kecil = muncul lebih dulu
              </p>
            </div>

            {/* Preview */}
            <div className="pt-4 border-t">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preview
              </label>
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg"
                style={{ backgroundColor: color + '20' }}
              >
                <span className="text-2xl">{icon}</span>
                <span className="font-medium" style={{ color }}>
                  {name || 'Nama Kategori'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
